import React, { ReactElement, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { makeStyles } from '@mui/styles';
import {
	FormControlLabel,
	IconButton,
	Pagination,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from '@mui/material';
import { colors, theme } from '../../theme';
import { CUMasterDataType, CUMaterialMappingType, CUMaterialRequestType } from '../../types/cu.types';
import { PostResponse } from '../../types/common.types';
import { CustomAlert } from '../shared/alert/alert';
import { CustomButton } from '../shared/button/button';
import { Loader } from '../shared/loader/loader';
import { MultiSelectDropDown } from '../shared/dropdown/multiSelect';
import { ToolTip } from '../shared/tooltip/tooltip';
import { Search } from '../shared/search/search';
import { Status, InScope, LabelGrade } from '../../config/cuTabConfig';
import { msalInstance } from '../../config/msalConfig';
import { SingleSelectDropDown } from '../shared/dropdown/singleSelect';
import { getMaterialMapping, saveMaterialMapping } from '../../services/cu-service';
import { useAsyncOperation } from '../../utils/use-async-operation';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

export const testid = 'raw-material';

const color = colors();
const themes = theme().typography;
export const RawMaterial = ({ master }: CUMasterDataType): ReactElement => {
	const classes = useStyles();
	const isAdmin: boolean = localStorage.getItem('roles')?.includes('TT.BrandSCAdmin') || false;
	const rawMaterialList = master.rawMaterial.map((item) => ({
		...item,
		name: item.name.replace('__', ' - '),
	}));
	const userAccount = msalInstance.getActiveAccount();
	const loggedInUser = userAccount ? `${userAccount.name?.split('(')[0]}| ${userAccount.username}` : '';

	const [initialData, setInitialData] = useState<CUMaterialMappingType[]>([]);
	const [materialData, setMaterialData] = useState<CUMaterialMappingType[]>([]);
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState<number>(25);
	const [pageCount, setPageCount] = useState<number>(1);
	const [openErrorAlert, setOpenErrorAlert] = useState(false);
	const [openSaveAlert, setOpenSaveAlert] = useState(false);
	const [rowData, setRowData] = useState<CUMaterialMappingType>();
	const [disableActions, setDisableActions] = useState<boolean>(false);
	const [rawMaterial, setRawMaterial] = useState<string[]>([]);
	const [labelGrade, setLabelGrade] = useState<string[]>([]);
	const [inScope, setInScope] = useState<string[]>([]);
	const [status, setStatus] = useState<string[]>([]);
	const [searchText, setSearchText] = useState<string>();
	const [showBlanks, setShowBlanks] = useState(false);
	const [material1, setMaterial1] = useState<string>();
	const [material2, setMaterial2] = useState<string>();
	const [grade, setGrade] = useState<string>();
	const [scope, setScope] = useState<boolean>(false);
	const initialLoadTriggered = useRef<boolean>(false);

	const [getMaterials, materialBusy, material, materialError] =
		useAsyncOperation<CUMaterialMappingType[]>(getMaterialMapping);
	const [saveMaterial, saveBusy, saveData, saveError] = useAsyncOperation<PostResponse>(saveMaterialMapping);

	useEffect(() => {
		if (initialLoadTriggered.current) return;
		initialLoadTriggered.current = true;
		getMaterials();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (materialError) setOpenErrorAlert(true);
	}, [materialError]);

	useEffect(() => {
		if (saveData) {
			getMaterials();
			setOpenSaveAlert(true);
			setDisableActions(false);
		}
	}, [saveData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (saveError) setOpenSaveAlert(true);
	}, [saveError]);

	useEffect(() => {
		if (material) {
			const sortedData: CUMaterialMappingType[] = [...material].sort(
				(a, b) => new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime()
			);
			setInitialData(sortedData);
			const filteredData = applyFilters(sortedData);
			setMaterialData(filteredData);
			setPageCount(Math.floor(filteredData.length / rowsPerPage));
		}
	}, [material]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (materialData) setPageCount(Math.floor(materialData.length / rowsPerPage));
	}, [rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setPage(1);
		const filteredData = applyFilters(initialData || []);
		setMaterialData(filteredData);
		setPageCount(Math.floor(filteredData.length / rowsPerPage));
	}, [showBlanks]); // eslint-disable-line react-hooks/exhaustive-deps

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	const handleRowPerPageChange = (value: string) => {
		setRowsPerPage(parseInt(value));
		setPage(1);
	};

	const onClearClick = () => {
		setPage(1);
		if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
			try {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			} catch {
				window.scrollTo(0, 0);
			}
		}
		setRawMaterial([]);
		setInScope([]);
		setStatus([]);
		setLabelGrade([]);
		setSearchText(undefined);
		setShowBlanks(false);
		setMaterialData(initialData || []);
		setPageCount(Math.floor(initialData.length / rowsPerPage));
	};

	const applyFilters = (sourceData: CUMaterialMappingType[]): CUMaterialMappingType[] => {
		let filteredData = sourceData || [];

		if (rawMaterial.length > 0 && filteredData.length > 0) {
			var rm = rawMaterial.map((mat) => mat.substring(0, mat.indexOf(' - ')));
			filteredData = filteredData.filter((d) =>
				rm.some((m) => d.cuMaterial1?.startsWith(m) || d.cuMaterial2?.startsWith(m))
			);
		}
		if (inScope.length === 1 && filteredData.length > 0) {
			filteredData = filteredData.filter((d) => (inScope[0] === 'Yes' ? d.inScope : !d.inScope));
		}
		if (status.length === 1 && filteredData.length > 0) {
			filteredData = filteredData.filter((d) => (status[0] === 'Active' ? d.active : !d.active));
		}
		if (labelGrade.length > 0 && filteredData.length > 0) {
			filteredData = filteredData.filter((d) => labelGrade.some((lg) => d.labelGrade?.includes(lg)));
		}
		if (searchText && filteredData.length > 0) {
			filteredData = filteredData.filter(
				(d) => d.materialName.toLowerCase().includes(searchText.toLowerCase()) || d.materialId.includes(searchText)
			);
		}
		if (showBlanks) {
			filteredData = filteredData.filter((d) => d.cuMaterial1 === null);
		}

		return filteredData;
	};

	const onSearchClick = () => {
		setPage(1);
		const filteredData = applyFilters(initialData || []);
		setMaterialData(filteredData);
		setPageCount(Math.floor(filteredData.length / rowsPerPage));
	};

	const handleAddEditClick = (row: CUMaterialMappingType, isEdit: boolean) => {
		let data = [...materialData];
		data.filter((d) => d.materialId === row.materialId)[0].isModify = true;
		if (isEdit) {
			data.filter((d) => d.materialId === row.materialId)[0].isEdit = true;
			setMaterial1(
				rawMaterialList.filter(
					(rm) =>
						rm.name.substring(0, rm.name.indexOf(' - ')) === row.cuMaterial1.substring(0, row.cuMaterial1.indexOf('__'))
				)[0]?.name || undefined
			);
			setMaterial2(
				rawMaterialList.filter(
					(rm) =>
						rm.name.substring(0, rm.name.indexOf(' - ')) === row.cuMaterial2.substring(0, row.cuMaterial2.indexOf('__'))
				)[0]?.name || undefined
			);
			setGrade(LabelGrade.filter((lg) => lg.value?.toString() === row.labelGrade)[0]?.key.toString() || undefined);
			setScope(row.inScope || false);
		}
		setMaterialData(data);
		setRowData(row);
		setDisableActions(true);
	};

	const onDiscardClick = (matId: string) => {
		let data = [...materialData];
		data.filter((d) => d.materialId === matId)[0].isModify = false;
		data.filter((d) => d.materialId === matId)[0].isEdit = false;
		setMaterialData(data);
		setDisableActions(false);
		setMaterial1(undefined);
		setMaterial2(undefined);
		setGrade(undefined);
		setScope(false);
	};

	const onSaveClick = () => {
		if (rowData && material1) {
			const requestData: CUMaterialRequestType = {
				id: rowData.isEdit ? rowData.id : null,
				materialId: rowData.materialId || '',
				materialName: rowData.materialName || '',
				cuMaterial1: material1.replace(' - ', '__'),
				cuMaterial2: material2 ? material2.replace(' - ', '__') : '',
				inScope: scope,
				labelGrade: grade
					? LabelGrade.filter((lg) => lg.key.toString() === grade.toString())[0]?.value?.toString() || ''
					: '',
				createdDate: rowData.isEdit ? rowData.createdDate : null,
				updatedBy: loggedInUser,
			};
			saveMaterial(requestData);
		}
	};

	return (
		<>
			<Loader key={`${testid}-loader`} open={materialBusy || saveBusy} message="Loading..." />
			{openErrorAlert && (
				<CustomAlert
					open={openErrorAlert}
					message="Something went wrong while retrieving material mapping data!"
					severity="error"
					timeout={5000}
					isFade={false}
					onCloseCallback={() => {
						setOpenErrorAlert(false);
					}}
				/>
			)}
			{openSaveAlert && (
				<div className={classes.alert}>
					<CustomAlert
						open={openSaveAlert}
						timeout={7000}
						message={saveData && !saveError ? 'Mapping saved successfully!' : 'Something went wrong!'}
						severity={saveData && !saveError ? 'success' : 'error'}
						onCloseCallback={() => {
							setOpenSaveAlert(false);
						}}
					/>
				</div>
			)}
			<div className={classes.filters}>
				{!showBlanks && (
					<MultiSelectDropDown
						label="Raw Material"
						width={185}
						data={rawMaterialList.map((list) => list.name)}
						value={rawMaterial}
						hasSearch
						onSelectCallback={setRawMaterial}
					/>
				)}
				{!showBlanks && (
					<MultiSelectDropDown
						label="In Scope"
						width={185}
						data={InScope.map((list) => list.value?.toString() || '')}
						value={inScope}
						onSelectCallback={setInScope}
					/>
				)}
				{!showBlanks && (
					<MultiSelectDropDown
						label="Label Grade"
						width={185}
						data={LabelGrade.map((list) => list.value?.toString() || '')}
						value={labelGrade}
						onSelectCallback={setLabelGrade}
					/>
				)}
				<MultiSelectDropDown
					label="Material Status"
					width={185}
					data={Status.map((list) => list.value?.toString() || '')}
					value={status}
					onSelectCallback={setStatus}
				/>
				<Search placeholder="H&M Material Name, ID" value={searchText || ''} onSearchCallback={setSearchText} />
				<CustomButton id="search" text="Search" width={90} variant="outlined" onClickCallback={onSearchClick} />
				{(rawMaterial.length > 0 ||
					inScope.length > 0 ||
					status.length > 0 ||
					labelGrade.length > 0 ||
					searchText !== undefined) && (
					<CustomButton id="clear" text="Clear" width={90} onClickCallback={onClearClick} />
				)}
				<FormControlLabel
					className={classes.label}
					control={
						<Switch
							checked={showBlanks}
							onClick={() => {
								setShowBlanks(!showBlanks);
								setPage(1);
							}}
							data-testid={`${testid}-blanks`}
						/>
					}
					label="Only Blanks"
					labelPlacement="start"
				/>
			</div>

			{materialData && (
				<>
					<TableContainer className={classes.table} data-testid={`${testid}-table`}>
						<Table stickyHeader>
							<TableHead>
								<TableRow key={`${testid}-header`}>
									<TableCell width="7%">Material ID</TableCell>
									<TableCell width="18%">Material Name</TableCell>
									<TableCell width="18%">TE Raw Material</TableCell>
									<TableCell width="18%">TE Raw Material</TableCell>
									<TableCell width="10%">In Scope for Standard claim</TableCell>
									<TableCell width="6%">Label grade</TableCell>
									<TableCell width="12%">Castor Activation Date</TableCell>
									<TableCell width="10%">Mapped Date</TableCell>
									{isAdmin && <TableCell width="1%" />}
								</TableRow>
							</TableHead>
							<TableBody data-testid={`${testid}-body`}>
								{(rowsPerPage > 0
									? materialData.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage)
									: materialData
								).map((row, i) => (
									<TableRow key={i} hover={row.isModify ? false : true}>
										{row.isModify ? (
											<>
												<TableCell className={classes.modifyRow}>{row.materialId}</TableCell>
												<TableCell>{row.materialName}</TableCell>
												<TableCell
													sx={{
														textWrap: 'nowrap',
														maxWidth: '100px',
													}}
												>
													<div>
														<div className={classes.divComp}>
															<SingleSelectDropDown
																labelText="Material 1"
																label=""
																data={rawMaterialList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={material1 || ''}
																width={325}
																hasSearch
																onSelectCallback={setMaterial1}
															/>
															<SingleSelectDropDown
																labelText="Material 2"
																label=""
																data={rawMaterialList.map((item) => ({
																	...item,
																	key: Number(item.keyId),
																	value: item.name,
																}))}
																value={material2 || ''}
																width={325}
																hasSearch
																onSelectCallback={setMaterial2}
															/>
														</div>
														<FormControlLabel
															className={classes.scopeSwitch}
															control={<Switch checked={scope} onClick={() => setScope(!scope)} />}
															label="In Scope for standard claim"
															labelPlacement="start"
														/>
														{scope && (
															<SingleSelectDropDown
																labelText="Label Grade"
																label=""
																data={LabelGrade}
																value={grade || ''}
																width={325}
																onSelectCallback={setGrade}
															/>
														)}
													</div>
													<div className={classes.actions}>
														{!material1 && (
															<CustomButton
																id="save-disabled"
																disabled={true}
																text="Save"
																data-testid={`${testid}-save-disabled`}
															/>
														)}
														{material1 && (
															<CustomButton
																id="save"
																text="Save"
																onClickCallback={onSaveClick}
																data-testid={`${testid}-save`}
															/>
														)}
														<CustomButton
															id="discard"
															text="Discard"
															onClickCallback={() => onDiscardClick(row.materialId)}
															data-testid={`${testid}-discard`}
														/>
													</div>
												</TableCell>
												<TableCell />
												<TableCell />
												<TableCell />
												<TableCell />
												<TableCell />
												<TableCell />
											</>
										) : (
											<>
												<TableCell className={!row.active ? classes.inactive : ''}>{row.materialId}</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													<ToolTip
														tooltipText={row.active ? '' : 'This Material is inactive'}
														children={<span>{row.materialName}</span>}
													/>
												</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													{row.cuMaterial1?.replace('__', ' - ')}
												</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													{row.cuMaterial2?.replace('__', ' - ')}
												</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													{row.inScope ? 'Yes' : ''}
												</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>{row.labelGrade || ''}</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													{row.activationDate ? dayjs(row.activationDate).format('DD/MM/YYYY') : ''}
												</TableCell>
												<TableCell className={!row.active ? classes.inactive : ''}>
													{row.updatedDate ? dayjs(row.updatedDate).format('DD/MM/YYYY') : ''}
												</TableCell>
												{isAdmin && (
													<TableCell align="right">
														{row.active && row.cuMaterial1 === null && (
															<ToolTip
																tooltipText="Add mapping"
																children={
																	<span>
																		<IconButton
																			id="add-mapping"
																			className={classes.iconButton}
																			disabled={disableActions}
																			onClick={() => handleAddEditClick(row, false)}
																		>
																			<AddIcon
																				fontSize="inherit"
																				sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																			/>
																		</IconButton>
																	</span>
																}
															/>
														)}
														{row.active && row.cuMaterial1 !== null && (
															<ToolTip
																tooltipText="Edit mapping"
																children={
																	<span>
																		<IconButton
																			id="edit-mapping"
																			className={classes.iconButton}
																			disabled={disableActions}
																			onClick={() => handleAddEditClick(row, true)}
																		>
																			<EditOutlinedIcon
																				sx={{ color: disableActions ? color.neutral[5] : color.primary[90] }}
																			/>
																		</IconButton>
																	</span>
																}
															/>
														)}
													</TableCell>
												)}
											</>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						data-testid={`${testid}-pagination`}
						className={classes.pagination}
						rowsPerPageOptions={[25, 50, 100]}
						rowsPerPage={rowsPerPage}
						count={materialData.length}
						page={page - 1}
						onPageChange={() => {}}
						onRowsPerPageChange={(e) => handleRowPerPageChange(e.target.value)}
						ActionsComponent={() => (
							<Pagination
								data-testid={`${testid}-node`}
								count={materialData.length % rowsPerPage === 0 ? pageCount : pageCount + 1}
								page={page}
								onChange={handlePageChange}
								shape="rounded"
							/>
						)}
					/>
				</>
			)}
		</>
	);
};

const useStyles = makeStyles(() => ({
	filters: {
		paddingTop: '10px',
		'& #search': {
			backgroundColor: color.primary[100],
			color: color.text.light,
		},
	},
	table: {
		height: 'calc(100% - 205px)',
		'& .MuiTableCell-head': {
			...themes.bodyMedium,
			color: color.neutral[8],
			backgroundColor: color.neutral[1],
			borderTop: `1px solid ${color.neutral[2]}`,
		},
		'& .MuiTableCell-body': {
			...themes.bodyRegular,
			color: color.text.dark,
			padding: '3px 8px !important',
		},
		'& .MuiTableCell-root': {
			borderBottom: `1px solid ${color.neutral[2]}`,
			padding: '13px 8px',
		},
		'& .MuiTableRow-hover': {
			'&:hover': {
				backgroundColor: `${color.signal.infoLight} !important`,
			},
		},
	},
	pagination: {
		'& .MuiToolbar-root.MuiTablePagination-toolbar': {
			position: 'absolute',
			right: 20,
			paddingTop: '5px',
		},
		'& .MuiButtonBase-root.Mui-selected': {
			backgroundColor: color.primary[100],
			color: color.shades.white,
		},
		'& .MuiButtonBase-root.Mui-selected:hover': {
			backgroundColor: color.primary[100],
		},
		'& .MuiPagination-root.MuiPagination-text': {
			flexShrink: 0,
			marginLeft: '10px',
		},
	},
	iconButton: {
		padding: '5px !important',
		'& svg': {
			width: '16px',
			height: '16px',
		},
	},
	inactive: {
		color: `${color.neutral[5]} !important`,
	},
	label: {
		position: 'absolute',
		right: '30px',
		paddingTop: '8px',
		'& .MuiTypography-root': {
			...themes.bodyRegular,
		},
	},
	modifyRow: {
		borderLeft: `5px solid ${color.signal.info}`,
	},
	actions: {
		'& #discard': {
			backgroundColor: color.primary[5],
			color: color.text.dark,
			border: 'none',
		},
		'& #save-disabled': {
			backgroundColor: color.neutral[6],
			color: color.text.light,
			border: 'none',
		},
		'& #save': {
			backgroundColor: color.primary[100],
			color: color.text.light,
			border: 'none',
		},
	},
	divComp: {
		display: 'flex',
		alignItems: 'center',
	},
	scopeSwitch: {
		marginLeft: '8px !important',
		'& .MuiTypography-root': {
			...themes.metaText,
		},
	},
	alert: {
		position: 'fixed',
		top: '10px',
		left: '50%',
		transform: 'translateX(-50%)',
	},
}));
